<template>
  <v-row>
    <v-col xl="8" class="ma-auto">
      <v-card flat tile>
        <h3 v-if="application" class="d-flex mb-2">
          <span>{{ $t('狀態') }}：</span>
          <status-chip :status="hasProivsioned ? APPLICATION_STATUS.PROVISIONED : application.status"></status-chip>
          <v-chip
            v-if="application.phone_count == 2"
            small
            class="ml-2"
          >{{ $t("第2門") }}</v-chip>
        </h3>
        <v-alert
          v-if="hasIssue && !hasProivsioned"
          border="left"
          color="error"
          text
          dense
          class="subtitle-2"
        >
          <p class="mb-0">{{ $t('問題件類型') }}：{{ $t(application.current_issue.type) }}</p>
          <p class="mb-0">{{ $t('問題件說明') }}：{{ parseIssueDescription(application.current_issue.description) }}</p>
        </v-alert>
        <ValidationObserver ref="form" slim>
          <v-form>
            <v-row no-gutters class="align-center">
              <v-col cols="12">
                <v-select
                  class="required"
                  prepend-icon="mdi-credit-card-plus-outline"
                  :label="$t('門號類型')"
                  :items="types"
                  item-text="text"
                  item-value="value"
                  v-model="form.type"
                ></v-select>
              </v-col>
              <v-col>
                <ValidationProvider
                  name="門號"
                  :rules="`required`"
                  v-slot="{ errors }"
                  :custom-messages="{
                    required: $t('門號 為必填'),
                  }"
                  ref="phoneInput"
                >
                  <v-text-field
                    type="tel"
                    class="required"
                    :label="$t('門號')"
                    prepend-icon="mdi-cellphone"
                    maxlength="10"
                    v-model="phone"
                    :error-messages="errors[0]"
                    :readonly="!!form.id"
                  ></v-text-field>
                </ValidationProvider>
                <ValidationProvider
                  name="卡號"
                  rules="required"
                  v-slot="{ errors }"
                  :custom-messages="{
                    required: $t('卡號 為必填'),
                  }"
                >
                  <v-text-field
                    class="required"
                    prepend-icon="mdi-sim"
                    :label="$t('卡號')"
                    v-model="cardNo"
                    @blur="findSim()"
                    :error-messages="errors[0]"
                    :readonly="!!form.id"
                  >
                  </v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="3" class="text-center" v-if="!form.id">
                <qr-code-scan-dialog @decode="decodeQRCode">
                  <template v-slot:activator="{ on }">
                    <v-btn color="primary" fab large dark v-on="on">
                      <v-icon>mdi-qrcode</v-icon>
                    </v-btn>
                  </template>
                </qr-code-scan-dialog>
              </v-col>
            </v-row>
            <v-alert
              text
              color="error"
              icon="mdi-alert"
              v-if="isCardNotFound"
            >
              {{ $t('此卡不在庫存內，請聯絡相關人員。') }}
            </v-alert>
            <v-row no-gutters>
              <v-col>
                <v-text-field
                  v-if="!hasProivsioned"
                  :label="$t('證號')"
                  v-model="form.identity_no"
                  prepend-icon="mdi-card-account-details-outline"
                />
                <v-text-field
                  type="tel"
                  :label="$t('聯絡電話')"
                  prepend-icon="mdi-phone"
                  v-model="form.phone"
                  v-mask="isAbroad ? '####################' : '##########'"
                ></v-text-field>
                <template v-if="isAbroad">
                  <v-autocomplete
                    :label="$t('仲介名')"
                    v-model="form.vendor_agent_id"
                    :items="agents"
                    item-text="name"
                    item-value="id"
                    prepend-icon="mdi-account"
                  />
                  <v-menu
                    ref="dateMenu"
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    :return-value.sync="form.provision_date"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <ValidationProvider
                        :name="$t('落地日期')"
                        vid="provision_date"
                        rules="required"
                        class="required"
                        v-slot="{ errors }"
                        :custom-messages="{
                          required: $t('落地日期及時間 為必填'),
                        }"
                      >
                        <v-text-field
                          :label="$t('落地日期')"
                          prepend-icon="mdi-calendar"
                          :value="form.provision_date"
                          readonly
                          v-on="on"
                          :error-messages="errors"
                          :disabled="!canEdit"
                        />
                      </ValidationProvider>
                    </template>
                    <v-date-picker
                      v-model="form.provision_date"
                      :min="minProvisionDate"
                      no-title
                      scrollable
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="dateMenu = false">{{ $t('取消') }}</v-btn>
                      <v-btn text color="primary" @click="$refs.dateMenu.save(form.provision_date)">OK</v-btn>
                    </v-date-picker>
                  </v-menu>
                  <v-row no-gutters>
                    <v-col>
                      <ValidationProvider
                        :name="$t('班機')"
                        vid="flight_id"
                        rules="required"
                        class="required"
                        v-slot="{ errors }"
                        :custom-messages="{
                          required: $t('班機為必填'),
                        }"
                      >
                        <v-autocomplete
                          :items="flights"
                          prepend-icon="mdi-airplane"
                          item-text="name"
                          item-value="id"
                          :label="$t('班機')"
                          v-model="form.flight_id"
                          :error-messages="errors"
                          :disabled="!canEdit"
                        />
                      </ValidationProvider>
                    </v-col>
                    <v-col
                      v-if="form.flight_id"
                    >
                      <v-text-field
                        :label="$t('表定抵達時間')"
                        :value="arrivalAt"
                        readonly
                      />
                    </v-col>
                  </v-row>
                </template>
                <ValidationProvider
                  v-else
                  :name="$t('預計開通日')"
                  vid="provision_date"
                  v-slot="{ errors }"
                >
                  <v-datetime-picker
                    :label="$t('預計開通日')"
                    v-model="form.provision_date"
                    :text-field-props="textFieldProps({errorMessages: errors})"
                    :time-picker-props="{ format: '24hr'}"
                    :date-picker-props="{ min: minProvisionDate }"
                    :disabled="!canEdit"
                    ref="datetimePicker"
                  >
                    <template slot="dateIcon">
                      <v-icon>mdi-calendar</v-icon>
                    </template>
                    <template slot="timeIcon">
                      <v-icon>mdi-clock</v-icon>
                    </template>
                    <template slot="actions">
                      <v-btn
                        color="primary"
                        text
                        @click="() => $refs.datetimePicker.clearHandler()"
                      >
                        {{ $t('取消') }}
                      </v-btn>
                      <v-btn
                        color="primary"
                        depressed
                        @click="() => $refs.datetimePicker.okHandler()"
                      >
                        OK
                      </v-btn>
                    </template>
                  </v-datetime-picker>
                </ValidationProvider>
                <v-text-field
                  :label="isAbroad ? $t('備註') : $t('備註')"
                  v-model="form.note"
                  prepend-icon="mdi-note-outline"
                  counter="255"
                >
                </v-text-field>
              </v-col>
            </v-row>
            <v-row v-if="!hasProivsioned">
              <v-col cols="12" class="pb-0">
                <v-img
                  v-if="signaturePreview"
                  :src="signaturePreview"
                  max-height="120"
                  contain
                >
                </v-img>
                <sign-pad-dialog
                  v-model="isSignPadDialogShown"
                  @sign="sign"
                >
                </sign-pad-dialog>
                <v-dialog
                  v-model="isCHTContractShown"
                  fullscreen
                  persistent
                  scrollable
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      depressed
                      block
                      large
                      color="primary"
                      v-on="on"
                      v-if="canEdit"
                    >
                      {{ signaturePreview ? $t('重簽名') : $t('看申請書並正楷簽名') }}
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-title>{{ $t('中華申請書') }}</v-card-title>
                    <v-carousel hide-delimiters hide-delimiter-background height="100%">
                      <v-carousel-item v-for="i in 8" :key="i">
                        <v-img
                          :src="require(`../../../img/contract-cht-page-${i}.jpg`)"
                          height="100%"
                          contain
                        ></v-img>
                      </v-carousel-item>
                    </v-carousel>
                    <v-card-actions>
                      <v-btn
                        depressed
                        block
                        color="primary"
                        @click="showSignPad"
                      >
                        {{ $t('請用正楷簽名') }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>

                <ValidationProvider name="signature" rules="required" v-slot="{ failed }">
                  <input type="hidden" v-model="signaturePreview" readonly tabindex="-1">
                  <v-alert
                    text
                    color="error"
                    icon="mdi-alert"
                    class="mt-2"
                    v-if="failed"
                  >
                    {{ $t('請閱讀申請書並簽名') }}
                  </v-alert>
                </ValidationProvider>

                <v-divider></v-divider>
                <ValidationProvider v-if="false" name="agree_to_join" :rules="{ required: { allowFalse: false } }" v-slot="{ failed }">
                  <div class="d-flex align-center" v-if="canEdit">
                    <v-checkbox v-model="form.agree_to_join" />
                    <i18n path="我已閱讀{term}並同意加入九亨會員">
                      <template v-slot:term>
                        <a href="#" @click.stop="isRuleShown = true">{{ $t('閱讀條款')}}</a>
                      </template>
                    </i18n>
                  </div>
                  <v-alert
                    v-if="failed"
                    color="error"
                    icon="mdi-alert"
                    text
                  >
                    {{ $t('請閱讀申請書並簽名') }}
                  </v-alert>
                </ValidationProvider>
                <v-dialog
                  v-model="isRuleShown"
                  persistent
                  max-width="600"
                >
                  <v-img :src="require(`../../../img/contract.jpg`)">
                  </v-img>
                  <v-btn
                    tile
                    large
                    block
                    color="primary"
                    @click.stop="isRuleShown = false"
                  >{{ $t('關閉') }}</v-btn>
                </v-dialog>

                <image-grid-uploader
                  ref="uploader"
                  :images="noSignatureImages"
                  :readonly="!canEdit || isLoading"
                  :camera-only="isPhotoUploadCameraOnly"
                  @change="files => this.files = files"
                  :max="7 - noSignatureImages.length"
                  @delete="onDeleteImage"
                >
                  <v-alert
                    color="warning"
                    class="mb-2"
                    icon="mdi-information"
                    text
                    dense
                    v-if="isAbroad"
                  >
                    {{ $t('護照、簽證(VISA)、勞動契約及全身照') }}<br>
                    {{ $t('3張以上,最多7張') }}
                  </v-alert>
                  <v-alert
                    v-else
                    color="warning"
                    class="mb-2"
                    icon="mdi-information"
                    text
                    dense
                  >
                    {{ $t('拍正反面：居留證、身份證、學生證、駕照') }}<br>
                    {{ $t('3張以上,最多7張') }}
                  </v-alert>
                </image-grid-uploader>

                <ValidationProvider
                  name="照片張數"
                  :rules="`required|min_value:${isAbroad ? 3 : 3}|max_value:7`"
                  :custom-messages="{
                    min_value: $t('照片張數 不得小於{0}', [isAbroad ? 3 : 3]),
                    max_value: $t('照片數不能超過 7 張'),
                  }"
                  v-slot="{ errors }"
                >
                  <input type="hidden" :value="totalImageCount" readonly tabindex="-1">
                  <v-alert
                    text
                    color="error"
                    icon="mdi-alert"
                    class="mt-2"
                    v-if="errors[0]"
                  >
                    {{ errors[0] }}
                  </v-alert>
                </ValidationProvider>
              </v-col>
            </v-row>
          </v-form>
        </ValidationObserver>
        <v-alert
          color="primary"
          text
          class="mb-0 mt-4"
          v-if="isLoading"
        >
          <p class="text-center">
            {{ $t('上傳中') }}
          </p>
          <v-progress-linear
            color="primary"
            indeterminate
            height="5"
          ></v-progress-linear>
        </v-alert>
        <v-card-actions class="flex-column pb-12" v-else-if="canEdit">
          <v-btn
            color="primary"
            class="ma-0 mt-4"
            large
            block
            depressed
            :loading="isLoading"
            @click="submit"
          >
            {{ $t('送件') }}
          </v-btn>
          <v-btn
            color="primary"
            block
            large
            depressed
            class="ma-0 mt-4"
            :loading="isLoading"
            @click="save"
          >
            {{ $t('暫存') }}
          </v-btn>
          <v-btn
            block
            large
            depressed
            color="error"
            class="ma-0 mt-4"
            :loading="isLoading"
            v-if="canRemove"
            @click="remove"
          >
            {{ $t('刪除') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-col>
    <v-dialog v-model="isSucessDialogShown" max-width="480">
      <v-card class="text-center" v-if="form">
        <v-card-title class="headline d-flex flex-column">
          <v-icon color="primary" x-large class="mb-5">mdi-checkbox-marked-circle</v-icon>
          {{ $t('送件完成') }}
        </v-card-title>
        <v-card-actions>
          <v-btn
            depressed
            color="primary"
            class="flex-grow-1"
            @click="$router.push({ name: 'apply.logs' })"
            v-if="id"
          >
            {{ $t('回辦門號紀錄') }}
          </v-btn>
          <v-btn
            depressed
            color="primary"
            @click="isSucessDialogShown = false"
            class="flex-grow-1"
            v-else
          >
            {{ $t('繼續辦門號') }}
          </v-btn>
          <v-btn
            depressed
            color="primary"
            class="flex-grow-1"
            @click="$router.push({ name: 'dashboard' })"
          >
            {{ $t('回主畫面') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { mask } from '@titou10/v-mask';
import moment from 'moment';
import QRCodeScanDialog from './components/QRCodeScanDialog';
import SignPadDialog from './components/SignPadDialog';
import ImageGridUploader from './components/ImageGridUploader';
import StatusChip from '../../components/StatusChip';
import { APPLICATION_STATUS } from '../../libs/const';
import { time } from '../../libs/filters';

const EMPTY_FORM = {
  id: null,
  type: 'apply',
  sim: null,
  phone: null,
  identity_no: null,
  vendor_agent_id: null,
  note: null,
  images: [],
  provision_date: null,
  agree_to_join: true,
  signature: null,
};


export default {
  props: {
    id: [String, Number],
  },

  directives: {
    mask,
  },

  filters: {
    time,
  },

  components: {
    'qr-code-scan-dialog': QRCodeScanDialog,
    StatusChip,
    SignPadDialog,
    ImageGridUploader,
    ValidationObserver,
    ValidationProvider,
  },

  data() {
    return {
      APPLICATION_STATUS,
      canEdit: true,
      isLoading: false,
      isSucessDialogShown: false,
      isCHTContractShown: false,
      isRuleShown: false,
      isSignPadDialogShown: false,
      dateMenu: false,
      modal: false,
      form: {...EMPTY_FORM},
      signaturePreview: null,
      files: [],
      maxProvisionDate: moment().add(1, 'month').format('YYYY-MM-DD'),
      isCardNotFound: false,
      phone: '',
      cardNo: null,
      application: null,
    }
  },

  computed: {
    canRemove() {
      if (this.application) {
        return this.application.status == APPLICATION_STATUS.ON_HOLD;
      }

      return false;
    },

    totalImageCount() {
      return this.noSignatureImages.length + this.files.length;
    },

    noSignatureImages() {
      return this.images.filter(image => image.key != 'signature');
    },

    images() {
      return _.get(this.application, 'images', []);
    },

    arrivalAt() {
      const flight = this.flights.find(flight => flight.id == this.form.flight_id);
      return flight ? time(flight.arrival_at) : null;
    },

    types() {
      return [
        {
          text: this.$t('一般門號'),
          value: 'apply',
        },
        // {
        //   text: "升級",
        //   value: 'upgrade',
        // },
        // {
        //   text: "換補卡",
        //   value: 'change',
        // },
      ];
    },

    availableSims() {
      if (_.get(this.application, 'sim')) {
        return [this.application.sim].concat(this.sims);
      }

      return this.sims;
    },

    minProvisionDate() {
      if (this.isAbroad) {
        return moment().format('YYYY-MM-DD');
      }

      return moment().add(1, 'day').format('YYYY-MM-DD');
    },

    hasIssue() {
      return this.application && this.application.status == APPLICATION_STATUS.HAS_ISSUE;
    },

    hasProivsioned() {
      return this.application && this.application.order_no != null;
    },

    isPhotoUploadCameraOnly() {
      return _.get(this.$store.state.user, 'vendor.photo_upload_camera_only', false);
    },

    ...mapGetters(['isAbroad']),

    ...mapState('sims', {
      sims: 'all'
    }),
    ...mapState('flights', {
      flights: 'all'
    }),
    ...mapState('agents', {
      agents: 'all'
    }),
  },

  watch: {
    'form.sim'(sim) {
      if (sim) {
        this.phone = sim.phone;
        this.cardNo = sim.card_no;
      }
    },

    phone() {
      if (this.phone.length < 10) {
        return;
      }

      this.findSim('phone', 'phone');
    },

    loading() {
      this.isLoading = this.loading;
    },

    application() {
      this.updateForm();
    },
  },

  beforeRouteUpdate (to, from, next) {
    if (!to.params.id) {
      this.$nextTick(() => {
        this.reset();
      });
    }

    next();
  },


  async mounted() {
    if (this.id) {
      this.isLoading = true;
      try {
        this.application = await this.fetchApplication(this.id);
        this.canEdit = [
          APPLICATION_STATUS.ON_HOLD,
          APPLICATION_STATUS.HAS_ISSUE,
        ].includes(this.application.status) && !this.hasProivsioned;
      } catch (error) {
        console.error(error)
        this.setSnackbar({
          text: this.$t("抱歉！找不到此暫存件！"),
          color: 'error',
        });

        this.$router.replace({
          name: 'dashboard',
        });
        return;
      }

      this.isLoading = false;
    }

    this.isLoading = true;
    await Promise.all([
      this.getFlights(),
      this.getAgents(),
      this.getSims({
        available: true,
      }),
    ]);
    this.isLoading = false;

    if (this.$route.params.no) {
      this.form.identity_no = this.$route.params.no;
    }

    this.updateForm();
  },

  methods: {
    textFieldProps(params) {
      return {...{
        class: this.isAbroad,
        prependIcon: "mdi-calendar",
        hint: this.isAbroad ? null : this.$t('如特定開通日期，才需點選'),
        persistentHint: true,
      }, ...params};
    },

    findSim(key = "card_no", thisKey = 'cardNo') {
      const sim = this.availableSims.find(sim => sim[key] == this[thisKey]);
      this.form.sim = sim;
      this.isCardNotFound = !sim;
    },

    decodeQRCode(result) {
      this.phone = result.phone;
      this.cardNo = result.card;
    },

    updateForm() {
      if (this.application) {
        this.form = _.cloneDeep(this.application);

        if (this.application.provision_date) {
          const date = moment(this.application.provision_date);
          this.form.provision_date = this.isAbroad ? date.format('YYYY-MM-DD') : date.toDate();
        }

        this.form.flight_id = this.application.flight ? this.application.flight.id : null;
        this.signaturePreview = _.get(this.images.filter(image => image.key == 'signature'), '0.url');

        this.form.vendor_agent_id = this.application.vendor_agent.id;
      }
    },

    async onDeleteImage(image) {
      if (!confirm(this.$t("確定刪除照片嗎？"))) {
        return ;
      }

      this.isLoading = true;
      this.application = await this.deleteImage({
        id: this.application.id,
        imageId: image.id
      });
      this.isLoading = false;
    },

    showSignPad() {
      this.isCHTContractShown = false;
      this.isSignPadDialogShown = true;
    },

    sign(base64Image, blob) {
      this.signaturePreview = base64Image;
      this.form.signature = blob;
    },

    reset() {
      this.form = {...EMPTY_FORM};
      this.application = null;
      this.phone = '';
      this.cardNo = null;
      this.provisionDate = null;
      this.isCardNotFound = false;
      this.signaturePreview = null;
      this.files = [];
      this.canEdit = true;
      this.isSucessDialogShown = false;
      this.isCHTContractShown = false;
      this.clearImages();

      this.$refs.form.reset();

      this.getSims({
        available: true,
      });
    },

    clearImages() {
      this.$refs.uploader.clear();
    },

    async save() {
      const result = await this.$refs.phoneInput.validate()
      if (!result.valid) {
        alert(result.errors[0]);
        return;
      }

      if (this.form.status != APPLICATION_STATUS.HAS_ISSUE) {
        this.form.status = APPLICATION_STATUS.ON_HOLD;
      }

      return this.store();
    },

    async submit() {
      const valid = await this.$refs.form.validate();
      if (!valid) {
        alert(this.$t('請檢查所有欄位是否填寫正確'));
        return;
      }

      this.form.status = 'submitted';
      await this.store();
    },

    async remove() {
      if (!confirm(this.$t('確定要刪除此筆？所有的資料都會消失，此動作不可復原？'))) {
        return;
      }

      this.isLoading = true;
      try {
        const id = this.application.id;
        await this.deleteApplication(this.application);
        this.setSnackbar({
          text: this.$t('刪除成功'),
        });

        this.$router.push({
          name: 'apply.logs'
        });
      } catch (error) {
        console.error(error);
        alert(this.$t('抱歉！刪除失敗，請稍後再試！'));
      }
      this.isLoading = false;
    },

    async store() {
      this.isLoading = true;

      try {
        let provisionDate = this.form.provision_date;
        if (provisionDate) {
          provisionDate = moment(this.form.provision_date).format('YYYY-MM-DD HH:mm:ss');

          const flight = this.flights.find(flight => flight.id == this.form.flight_id)
          if (flight) {
            provisionDate = moment(provisionDate).format('YYYY-MM-DD') + ' ' + flight.arrival_at;
          }
        }
        let form = {...this.form, ...{
          provision_date: provisionDate,
          images: this.files,
          // Remove the sim id if the application has been created.
          sim_id: this.form.id ? null : this.form.sim.id,
        }};

        const action = form.id ? 'updateApplication' : 'createApplication';
        const application = await this[action](form);
        this.reset();

        if (form.status == APPLICATION_STATUS.SUBMITTED) {
          this.isSucessDialogShown = true;
        } else {
          this.setSnackbar({
            text: this.$t('暫存成功'),
          });

          if (this.id) {
            this.$router.push({
              name: 'apply'
            });
          }
        }
      } catch (error) {
        console.error(error);
        alert(this.$t("抱歉！無法暫存，請稍後再試！"));
      }

      this.isLoading = false;
    },

    parseIssueDescription(description) {
      const match = description ? description.match(/\[(OR\d{3}[A-Z0-9]?|\d{3})\]/) : null;
      return match ? this.$t(match[0]) : description;
    },

    ...mapActions(['setSnackbar']),
    ...mapActions('applications', {
      createApplication: 'create',
      updateApplication: 'update',
      deleteApplication: 'delete',
      fetchApplication: 'fetch',
      deleteImage: 'deleteImage',
    }),
    ...mapActions('sims', {
      getSims: 'get'
    }),
    ...mapActions('flights', {
      getFlights: 'get'
    }),
    ...mapActions('agents', {
      getAgents: 'get'
    }),
  },
}
</script>

<style lang="scss" scoped>
::v-deep .required label.v-label.theme--light {
  &:before {
    content: '*';
    color: red;
  }
}
</style>
